import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { businessTermLabel } from '@erp/shared';
import { SearchCombobox } from '@/components/common/search-combobox';
import { usePermissions } from '@/features/auth';
import { useBusinessTerms } from '../api/use-business-terms';

export function DeliveryTermSelect({
  id,
  value,
  onChange,
  customerTerms,
  disabled = false,
  ariaInvalid,
  ariaDescribedBy,
}: {
  id: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  customerTerms?: string | null | undefined;
  disabled?: boolean;
  ariaInvalid?: boolean | undefined;
  ariaDescribedBy?: string | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const query = useBusinessTerms();
  const [search, setSearch] = useState('');
  const terms = (query.data?.data ?? []).filter(
    (row) =>
      row.kind === 'DELIVERY' && (row.scope === 'SALES' || row.scope === 'BOTH') && row.isActive,
  );
  const selected = terms.find((row) => row.name === value);
  const inherited =
    customerTerms === undefined
      ? t('conditions.none')
      : t('conditions.inherit', { value: customerTerms || '—' });
  const options = [
    { id: 'INHERIT', label: inherited },
    ...terms.map((row) => ({
      id: row.id,
      label: businessTermLabel(row, i18n.language),
      hint: row.isDefault ? t('conditions.default') : undefined,
    })),
    ...(value && !selected ? [{ id: 'LEGACY', label: value, hint: t('conditions.legacy') }] : []),
  ].filter(
    (option) =>
      option.id === 'INHERIT' ||
      option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  return (
    <div className="space-y-2">
      <SearchCombobox
        id={id}
        ariaInvalid={ariaInvalid}
        ariaDescribedBy={ariaDescribedBy}
        value={selected ? businessTermLabel(selected, i18n.language) : value || inherited}
        placeholder={t('conditions.search')}
        ariaLabel={t('conditions.deliveryField')}
        options={options}
        isLoading={query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        disabled={disabled}
        emptyLabel={t('conditions.empty')}
        onQueryChange={setSearch}
        onSelect={(option) => {
          if (option.id === 'LEGACY') return;
          onChange(
            option.id === 'INHERIT'
              ? null
              : (terms.find((row) => row.id === option.id)?.name ?? null),
          );
          setSearch('');
        }}
      />
      {can('settings:read') ? (
        <Link
          className="inline-flex min-h-11 items-center text-sm text-muted-foreground underline underline-offset-4"
          to="/settings/conditions?kind=DELIVERY"
        >
          {t('conditions.manage')}
        </Link>
      ) : null}
    </div>
  );
}
